/**
 * Contratti dei gruppi di lavoro e dei blocchi sulla seduta.
 *
 * Il mix del blocco è lo snapshot della scheda: non arriva nel body.
 * RPE e minuti si fissano solo all'inserimento.
 */

import { z } from 'zod';
import { idSchema, normalizzaNome } from './contratti.js';

export const creaGruppoSchema = z.object({
  nome: z
    .string()
    .trim()
    .min(1, 'Il nome è obbligatorio.')
    .max(80, 'Massimo 80 caratteri.')
    .transform(normalizzaNome),
});

export type DatiGruppo = z.infer<typeof creaGruppoSchema>;

export const modificaGruppoSchema = creaGruppoSchema;
export type DatiModificaGruppo = z.infer<typeof modificaGruppoSchema>;

export const giocatoriGruppoSchema = z.object({
  giocatoreIds: z.array(idSchema),
});

export type DatiGiocatoriGruppo = z.infer<typeof giocatoriGruppoSchema>;

/** Sposta un giocatore in un gruppo: una sola scrittura, senza toglierlo prima. */
export const spostaGiocatoreSchema = z.object({
  gruppoId: idSchema,
});

export type DatiSpostaGiocatore = z.infer<typeof spostaGiocatoreSchema>;

const rpeTarget = z
  .number({ invalid_type_error: 'RPE deve essere un intero tra 1 e 10.' })
  .int()
  .min(1, 'RPE deve essere un intero tra 1 e 10.')
  .max(10, 'RPE deve essere un intero tra 1 e 10.');

const minutiProgrammati = z
  .number({ invalid_type_error: 'I minuti devono essere un intero maggiore di 0.' })
  .int()
  .min(1, 'I minuti devono essere un intero maggiore di 0.')
  .max(32767, 'I minuti superano il massimo.');

/**
 * Un blocco nasce da una scheda o da un test di misura con RPE e minuti di
 * riferimento: l'uno o l'altro.
 */
export const creaBloccoSedutaSchema = z
  .object({
    schedaId: idSchema.optional(),
    testId: idSchema.optional(),
    gruppoIds: z.array(idSchema).min(1, 'Scegli almeno un gruppo.'),
    rpeTarget: rpeTarget.optional(),
    minutiProgrammati: minutiProgrammati.optional(),
  })
  .refine((d) => (d.schedaId === undefined) !== (d.testId === undefined), {
    message: 'Scegli una scheda o un test.',
    path: ['schedaId'],
  });

export type DatiBloccoSeduta = z.infer<typeof creaBloccoSedutaSchema>;

const minutiEseguiti = z
  .number({ invalid_type_error: 'I minuti devono essere un intero maggiore o uguale a 0.' })
  .int()
  .min(0, 'I minuti devono essere un intero maggiore o uguale a 0.')
  .max(32767, 'I minuti superano il massimo.');

const rpeEseguito = z
  .number({ invalid_type_error: 'RPE deve essere un intero tra 1 e 10.' })
  .int()
  .min(1, 'RPE deve essere un intero tra 1 e 10.')
  .max(10, 'RPE deve essere un intero tra 1 e 10.');

/** Una riga del Registro seduta: minuti e RPE eseguiti, motivo opzionale. */
export const logBloccoSchema = z.object({
  minutiEseguiti,
  rpeEseguito,
  motivo: z
    .string()
    .trim()
    .max(200, 'Massimo 200 caratteri.')
    .optional()
    .transform((v) => (v === '' ? undefined : v)),
});

export type DatiLogBlocco = z.infer<typeof logBloccoSchema>;

/** DELETE del blocco con Registro seduta: `?conferma=true`. */
export const confermaEliminaBloccoSchema = z.object({
  conferma: z.literal('true').optional(),
});
